/**
 * ChatCsvImportModal — the chat-driven CSV import modal lifted out of ChatBox's
 * JSX (delegation pattern). The <ImportCSVModal> JSX + its full onImport handler
 * body are reproduced VERBATIM from ChatBox: workspace contacts-module
 * lookup/create, per-row draft mapping, the sequential api.objects.createObject
 * loop, the summary toast, and contact-query invalidation.
 *
 * QUARANTINE NOTE: this re-homes the already-present `@/modules/contacts`
 * ImportCSVModal import from ChatBox. ChatBox + ChatModulePane are the
 * allowlisted cross-layer-import loci; this file lives under chatbox/ and only
 * moves that one already-existing import, so it keeps the quarantine contained
 * rather than spreading a new cross-layer site. No raw fetch — uses api.* only.
 */
import { ImportCSVModal, type ImportedCSVContact } from '@/modules/contacts/ImportCSVModal';
import { api } from '@/lib/api';
import { MODULE_ALIASES } from '@/types/modules';
import { buildImportFollowUps, pickTopTag } from '@/features/chat/utils/import-followups';
import type { QuickAction } from '@/features/chat/hooks/useChat.types';
import type { QueryClient } from '@tanstack/react-query';

export interface ChatCsvImportModalProps {
  file: File;
  token: string | null;
  workspaceId: string;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  queryClient: QueryClient;
  onClose: () => void;
  /**
   * Surface result-aware follow-up chips after a successful import (#2014).
   * Called with 3-5 QuickActions referencing the real imported count + top tag.
   */
  onFollowUps?: (actions: QuickAction[]) => void;
}

export function ChatCsvImportModal({
  file,
  token,
  workspaceId,
  showToast,
  queryClient,
  onClose,
  onFollowUps,
}: ChatCsvImportModalProps) {
  return (
    <ImportCSVModal
      initialFile={file}
      onClose={onClose}
      onImport={async (contacts: ImportedCSVContact[]) => {
        // Mirror the per-row path the ⋯ menu's modal uses: POST to
        // the type-specific /contacts endpoint (via api.objects.createObject)
        // for each row. /bulk/objects has a different payload shape
        // (expects `title` not `name`) and would silently create
        // contacts with title='Untitled', so we skip it here.
        //
        // Contacts MUST be linked to at least one module (enforced at
        // apps/api/src/routes/objects.ts:466). Look up any contacts
        // module in the workspace; if none exists, create one. Workspace-
        // level lookup is intentional — the user might be importing
        // into a conversation that has no Contacts panel attached, but
        // a Contacts module elsewhere in the workspace is the right
        // home for these records.
        let contactsModuleId: string | null = null;
        if (workspaceId) {
          try {
            const wsModules = await api.workspaces.listWorkspaceModules(token, workspaceId);
            const existing = wsModules.find((m: any) =>
              ((MODULE_ALIASES[m.type] ?? m.type) as string) === 'contact' && !m.archived && !m.archivedAt
            );
            if (existing) {
              contactsModuleId = existing.id;
            } else {
              const created = await api.workspaces.createWorkspaceModule(token, workspaceId, {
                type: 'contact',
                label: 'Contacts',
              }, { idempotencyKey: crypto.randomUUID() });
              contactsModuleId = created.id;
            }
          } catch (err) {
            showToast(`Couldn't access Contacts module: ${err instanceof Error ? err.message : String(err)}`, 'error');
            return;
          }
        }
        if (!contactsModuleId) {
          showToast('No workspace selected — open a workspace and try again.', 'error');
          return;
        }
        // Keep each draft paired with its source row so the top-tag tally can be
        // scoped to the rows that were actually created (see the loop below).
        const drafts = contacts
          .map((c) => {
            const name = (c.name || '').trim();
            const emails = Array.isArray(c.emails) ? c.emails.filter(Boolean) : [];
            const fallback = emails[0] || '';
            const finalName = name || fallback;
            if (!finalName) return null;
            const data: any = {
              type: 'contact' as const,
              name: finalName,
              stage: 'lead',
              moduleLinks: [{ moduleId: contactsModuleId }],
            };
            if (emails.length) data.emails = emails;
            if (Array.isArray(c.phones) && c.phones.length) data.phones = c.phones;
            if (c.organization) data.organization = c.organization;
            if (c.role) data.role = c.role;
            if (c.relationship) data.relationship = c.relationship;
            if (c.status) data.status = c.status;
            if (c.location) data.location = c.location;
            if (c.birthday) data.birthday = c.birthday;
            if (Array.isArray(c.tags) && c.tags.length) data.tags = c.tags;
            if (c.summary) data.summary = c.summary;
            if (Array.isArray(c.notes) && c.notes.length) {
              data.notes = c.notes.map((content) => ({
                id: crypto.randomUUID(),
                content,
                createdAt: new Date().toISOString(),
                authorId: 'system',
                authorName: 'CSV import',
              }));
            }
            return { data, source: c };
          })
          .filter((d): d is NonNullable<typeof d> => d !== null);
        if (drafts.length === 0) {
          showToast('No valid contacts to import (every row missing name + email).', 'error');
          return;
        }
        let successes = 0;
        let failures = 0;
        let firstError: string | null = null;
        // Source rows whose API create actually succeeded — the only rows that
        // exist in the system, so the only honest input for the top-tag tally.
        const importedRows: ImportedCSVContact[] = [];
        for (const { data, source } of drafts) {
          try {
            await api.objects.createObject(token, data);
            successes++;
            importedRows.push(source);
          } catch (err) {
            failures++;
            if (!firstError) firstError = err instanceof Error ? err.message : String(err);
          }
        }
        const summary = failures === 0
          ? `Imported ${successes} contact${successes === 1 ? '' : 's'}.`
          : `Imported ${successes} contact${successes === 1 ? '' : 's'} (${failures} failed${firstError ? `: ${firstError.slice(0, 80)}` : ''}).`;
        showToast(summary, failures === 0 ? 'success' : 'error');
        queryClient.invalidateQueries({
          predicate: (q) => JSON.stringify(q.queryKey).includes('contact'),
        });
        // After a successful import, surface result-aware follow-up chips so the
        // chat doesn't go silent at the most engaged moment (#2014). topTag is
        // tallied from `importedRows` (rows that actually got created), NOT the
        // raw CSV — otherwise a tag dominant in invalid/failed rows could label
        // the "Tag all N with <tag>" chip even though none of the N exist with it.
        if (successes > 0) {
          const topTag = pickTopTag(importedRows);
          onFollowUps?.(
            buildImportFollowUps({ imported: successes, failed: failures, objectType: 'contact', topTag }),
          );
        }
        onClose();
      }}
    />
  );
}
